/**
 * fieldTeamView.ts — pure view-model for Operations / Agents (M7b, the
 * field-team page).
 *
 * Joins the active roster (GET /v1/hr/employees?status=active) with the
 * CURRENT month's field tasks (the M5b windowed fetch) BY ASSIGNEE ID —
 * never by name — so each profile card's "Tasks this month" is a true
 * month figure (the mockup's own count was all-time under a month label).
 *
 * FieldTeamCard names every field the card may render, one by one: the
 * employee list wire also carries payroll decoration (current.
 * annualSalaryPence) and dateOfBirth, and this page must provably ignore
 * both — the structural pick here plus the page's disclosure test are that
 * proof. Empty identity values normalize to undefined so the card's
 * omit-empty rendering has one honest signal (the wire omits absent
 * fields; '' is defensively treated the same — no blank rows).
 */
import type { ApiEmployeeDirectoryRow, OpsTask } from '../../types'
import { teamTracker, trackerInitials } from './tasksView'
import { monthGrid, monthOf } from './calendarMath'

export interface FieldTeamCard {
  id: string
  initials: string
  name: string
  /** The 0104 identity trio (M7a) — undefined rows are omitted, never dashed. */
  jobTitle?: string
  employeeNumber: string
  email?: string
  phone?: string
  region?: string
  status: string
  /** Tasks assigned to this person in the fetched month window / completed
   *  among them (teamTracker's definitions — the Team Tracker card's math). */
  total: number
  completed: number
}

/** The month containing `now` as inclusive fetch bounds + display label.
 *  Derived from calendarMath's grid TRIMMED to in-month cells: gridStart/
 *  gridEnd cover the visible grid's other-month edge days, which would
 *  leak neighbouring-month tasks into "Tasks this month". */
export function monthWindow(now: Date): { from: string; to: string; label: string } {
  const { year, month } = monthOf(now)
  const grid = monthGrid(year, month, now)
  const inMonth = grid.cells.filter(c => c.inMonth)
  return { from: inMonth[0].date, to: inMonth[inMonth.length - 1].date, label: grid.label }
}

/** The header count line — people, honestly: the roster is active employees,
 *  not a "field agents" claim the data cannot back. */
export function teamCountLine(n: number): string {
  return `${n} ${n === 1 ? 'person' : 'people'} on the team.`
}

/** One card per employee, stats via the shipped teamTracker over that
 *  person's own slice of the month's tasks (assignedTo === employee id).
 *  A person with no tasks is an honest 0/0 card, never dropped; tasks
 *  assigned to nobody (or to a non-roster id) attach to no card. */
export function buildFieldTeam(employees: ApiEmployeeDirectoryRow[], tasks: OpsTask[]): FieldTeamCard[] {
  return employees.map(e => {
    const own = teamTracker(tasks.filter(t => t.assignedTo === e.id))
    return {
      id: e.id,
      initials: trackerInitials(e.fullName),
      name: e.fullName,
      jobTitle: e.jobTitle || undefined,
      employeeNumber: e.employeeNumber,
      email: e.email || undefined,
      phone: e.phone || undefined,
      region: e.region || undefined,
      status: e.status,
      total: own.total,
      completed: own.completed,
    }
  })
}
